import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {DotsThree} from '../../icons';

// Lo mínimo que el menú deja entre su borde y el de la pantalla.
const VIEWPORT_MARGIN = 12;

/**
 * El «⋯» de una fila o del editor (UD-18): lo que se usa poco, a un clic. `items`:
 * `[{key, label, icon?, onSelect?, to?, danger?, disabled?, hint?, separator?}]` — `to` es un link, `separator` pone una
 * raya antes. Sin ítems no se dibuja.
 *
 * Se cierra con un clic afuera, al elegir y con Escape (el foco vuelve al botón). Escape no sigue de largo: en el editor,
 * el mismo Escape cancelaría la edición entera. Flechas arriba y abajo recorren los ítems.
 */
export function RowMenu({items, label, testId = 'alc-row-more', align = 'right', reserve = false}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    const close = useCallback(({focusButton = false} = {}) => {
        setOpen(false);
        if (focusButton && buttonRef.current) buttonRef.current.focus();
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) close();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, close]);

    // Al abrir, el foco va al primer ítem que se puede elegir.
    useEffect(() => {
        if (!open || !menuRef.current) return;
        const first = menuRef.current.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
        if (first) first.focus({preventScroll: true});
    }, [open]);

    // El menú se corre lo que haga falta para quedar entero en la pantalla: alineado a la derecha de un «⋯» que está a la
    // izquierda (el editor de una de Dashbo a 390), se salía por el borde (QA-VIS-ud18-161).
    const [shift, setShift] = useState(0);
    useLayoutEffect(() => {
        if (!open || !menuRef.current) {
            setShift(0);
            return;
        }
        const rect = menuRef.current.getBoundingClientRect();
        if (!rect.width) return;
        const viewport = document.documentElement.clientWidth || window.innerWidth;
        if (rect.left < VIEWPORT_MARGIN) setShift(VIEWPORT_MARGIN - rect.left);
        else if (rect.right > viewport - VIEWPORT_MARGIN) setShift(viewport - VIEWPORT_MARGIN - rect.right);
    }, [open]);

    // Sin ítems no hay «⋯»; en una lista (`reserve`) queda su lugar, así «Editar» no se corre de una fila a otra.
    if (!items || items.length === 0) return reserve ? <span className="alc-mr-more -empty" aria-hidden="true"/> : null;

    const onKeyDown = (event) => {
        if (!open) return;
        if (event.key === 'Escape') {
            event.stopPropagation();
            event.preventDefault();
            close({focusButton: true});
            return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const entries = [...menuRef.current.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])')];
        if (!entries.length) return;
        event.preventDefault();
        const at = entries.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? (at + 1) % entries.length : (at - 1 + entries.length) % entries.length;
        entries[next].focus();
    };

    const renderItem = (item) => {
        const className = `alc-mr-menu__item${item.danger ? ' -danger' : ''}`;
        const content = (
            <>
                {item.icon && <span className="alc-mr-menu__icon" aria-hidden="true">{item.icon}</span>}
                <span className="alc-mr-menu__label">{item.label}</span>
                {item.hint && <small className="alc-mr-menu__hint">{item.hint}</small>}
            </>
        );
        if (item.disabled) {
            return (
                <span key={item.key} role="menuitem" aria-disabled="true" tabIndex={-1} className={`${className} -disabled`}
                      data-menu-item={item.key}>
                    {content}
                </span>
            );
        }
        if (item.to) {
            return (
                <Link key={item.key} role="menuitem" to={item.to} className={className} data-menu-item={item.key}
                      onClick={() => close()}>
                    {content}
                </Link>
            );
        }
        return (
            <button key={item.key} type="button" role="menuitem" className={className} data-menu-item={item.key}
                    onClick={() => { close({focusButton: true}); item.onSelect(); }}>
                {content}
            </button>
        );
    };

    return (
        <div className="alc-mr-more" ref={rootRef} onKeyDown={onKeyDown}>
            <button type="button" ref={buttonRef} className="alc-icon-btn" aria-label={label} aria-haspopup="menu"
                    aria-expanded={open} data-testid={testId} onClick={() => setOpen((value) => !value)}>
                <DotsThree size={18} weight="bold" aria-hidden="true"/>
            </button>
            {open && (
                <div className={`alc-mr-menu -${align}`} role="menu" aria-label={label} ref={menuRef}
                     style={shift ? {transform: `translateX(${Math.round(shift)}px)`} : undefined}>
                    {items.map((item) => (
                        <React.Fragment key={item.key}>
                            {item.separator && <div className="alc-mr-menu__sep" role="separator"/>}
                            {renderItem(item)}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}

export default RowMenu;
